import Logger from './logger';
import dataEncryptionService, { DataClassification } from './dataEncryptionService';
import dataMaskingService from './dataMaskingService';
import centralizedAuditLogService from './centralizedAuditLogService';

// Secure storage configuration
interface SecureStorageConfig {
  enableEncryption: boolean;
  enableMasking: boolean;
  autoClassify: boolean;
  auditLogging: boolean;
  retentionPeriod: number; // in days
}

// Storage operation result
interface StorageOperationResult {
  success: boolean;
  operationId: string;
  timestamp: number;
  encrypted: boolean;
  masked: boolean;
  classification: DataClassification;
  error?: string;
}

// Data audit log entry
interface AuditLogEntry {
  id: string;
  operation: 'store' | 'retrieve' | 'update' | 'delete' | 'encrypt' | 'decrypt' | 'mask' | 'unmask';
  fieldName?: string;
  classification: DataClassification;
  timestamp: number;
  userId?: string;
  success: boolean;
  error?: string;
  metadata?: any;
}

// Field security configuration
interface FieldSecurityConfig {
  fieldName: string;
  classification: DataClassification;
  encrypt: boolean;
  mask: boolean;
  required: boolean;
  validationPattern?: RegExp;
}

class SecureDataStorageService {
  private config: SecureStorageConfig;
  private auditLog: AuditLogEntry[];
  private fieldConfigs: Map<string, FieldSecurityConfig>;
  private isInitialized: boolean = false;

  constructor() {
    this.config = {
      enableEncryption: true,
      enableMasking: true,
      autoClassify: true,
      auditLogging: true,
      retentionPeriod: 90
    };
    this.auditLog = [];
    this.fieldConfigs = new Map();
    this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      // Load field configurations from localStorage
      await this.loadFieldConfigurations();
      this.isInitialized = true;
      Logger.info('Secure data storage service initialized successfully');
    } catch (error) {
      Logger.error('Failed to initialize secure storage service', error instanceof Error ? error : new Error('Unknown error'));
      this.isInitialized = false;
    }
  }

  private async loadFieldConfigurations(): Promise<void> {
    try {
      const storedConfigs = localStorage.getItem('field_security_configs');
      if (storedConfigs) {
        const configs = JSON.parse(storedConfigs);
        configs.forEach((config: FieldSecurityConfig) => {
          this.fieldConfigs.set(config.fieldName, config);
        });
        Logger.info(`Loaded ${configs.length} field security configurations`);
      } else {
        // Set up default configurations for common fields
        this.setupDefaultFieldConfigurations();
      }
    } catch (error) {
      Logger.error('Failed to load field configurations', error instanceof Error ? error : new Error('Unknown error'));
      this.setupDefaultFieldConfigurations();
    }
  }

  private setupDefaultFieldConfigurations(): void {
    const defaultConfigs: FieldSecurityConfig[] = [
      {
        fieldName: 'email',
        classification: DataClassification.RESTRICTED,
        encrypt: true,
        mask: true,
        required: true,
        validationPattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      },
      {
        fieldName: 'phone',
        classification: DataClassification.RESTRICTED,
        encrypt: true,
        mask: true,
        required: false,
        validationPattern: /^[\+]?[1-9][\d]{0,15}$/
      },
      {
        fieldName: 'address',
        classification: DataClassification.CONFIDENTIAL,
        encrypt: true,
        mask: true,
        required: false
      },
      {
        fieldName: 'name',
        classification: DataClassification.CONFIDENTIAL,
        encrypt: true,
        mask: true,
        required: true
      },
      {
        fieldName: 'company',
        classification: DataClassification.CONFIDENTIAL,
        encrypt: false,
        mask: false,
        required: false
      },
      {
        fieldName: 'rate',
        classification: DataClassification.CONFIDENTIAL,
        encrypt: true,
        mask: false,
        required: false
      },
      {
        fieldName: 'status',
        classification: DataClassification.PUBLIC,
        encrypt: false,
        mask: false,
        required: false
      }
    ];

    defaultConfigs.forEach(config => {
      this.fieldConfigs.set(config.fieldName, config);
    });

    this.saveFieldConfigurations();
  }

  private saveFieldConfigurations(): void {
    try {
      const configs = Array.from(this.fieldConfigs.values());
      localStorage.setItem('field_security_configs', JSON.stringify(configs));
    } catch (error) {
      Logger.error('Failed to save field configurations', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  logAuditEntry(entry: Omit<AuditLogEntry, 'id' | 'timestamp'>): void {
    if (!this.config.auditLogging) return;

    const auditEntry: AuditLogEntry = {
      id: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      ...entry
    };

    // Keep local audit log for backward compatibility
    this.auditLog.push(auditEntry);

    // Clean up old audit logs
    const cutoffTime = Date.now() - (this.config.retentionPeriod * 24 * 60 * 60 * 1000);
    this.auditLog = this.auditLog.filter(entry => entry.timestamp > cutoffTime);

    // Log to centralized audit log service
    try {
      const operationMap: Record<string, 'create' | 'read' | 'update' | 'delete' | 'other'> = {
        'store': 'create',
        'retrieve': 'read',
        'update': 'update',
        'delete': 'delete',
        'encrypt': 'other',
        'decrypt': 'other',
        'mask': 'other',
        'unmask': 'other'
      };

      centralizedAuditLogService.logEntry({
        userId: entry.userId,
        action: `secure_storage_${entry.operation}`,
        resource: 'secure_data_storage',
        resourceId: entry.fieldName,
        operation: operationMap[entry.operation] || 'other',
        result: entry.success ? 'success' : 'failure',
        severity: entry.classification === 'restricted' ? 'high' : entry.classification === 'confidential' ? 'medium' : 'low',
        category: 'data_modification',
        details: {
          operation: entry.operation,
          classification: entry.classification,
          fieldName: entry.fieldName,
          error: entry.error,
          metadata: entry.metadata
        }
      }).catch(error => {
        Logger.error('Failed to log to centralized audit log', error instanceof Error ? error : new Error('Unknown error'));
      });
    } catch (error) {
      Logger.error('Failed to log to centralized audit log', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  // Public API methods
  async secureStore(document: any, collectionName: string, userId?: string): Promise<StorageOperationResult> {
    if (!this.isInitialized) {
      throw new Error('Secure storage service not initialized');
    }

    const operationId = `store_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    let encrypted = false;
    let masked = false;
    let classification = DataClassification.PUBLIC;

    try {
      const securedDoc = { ...document };
      const fieldsToEncrypt: string[] = [];
      const fieldsToMask: string[] = [];

      // Process each field based on configuration
      for (const [fieldName, value] of Object.entries(document)) {
        if (typeof value === 'string' && value.trim()) {
          const fieldConfig = this.fieldConfigs.get(fieldName);
          let fieldClassification = DataClassification.PUBLIC;

          if (fieldConfig) {
            fieldClassification = fieldConfig.classification;
            
            if (fieldConfig.encrypt && this.config.enableEncryption) {
              fieldsToEncrypt.push(fieldName);
            }
            
            if (fieldConfig.mask && this.config.enableMasking) {
              fieldsToMask.push(fieldName);
            }
          } else if (this.config.autoClassify) {
            // Auto-classify based on field name and content
            fieldClassification = dataEncryptionService.classifyField(fieldName, value);
            
            if (fieldClassification === DataClassification.RESTRICTED || fieldClassification === DataClassification.CONFIDENTIAL) {
              if (this.config.enableEncryption) {
                fieldsToEncrypt.push(fieldName);
              }
              if (this.config.enableMasking) {
                fieldsToMask.push(fieldName);
              }
            }
          }

          // Update overall classification
          if (fieldClassification > classification) {
            classification = fieldClassification;
          }
        }
      }

      // Apply encryption
      if (fieldsToEncrypt.length > 0) {
        try {
          const encryptedDoc = await dataEncryptionService.encryptDocument(securedDoc, fieldsToEncrypt);
          Object.assign(securedDoc, encryptedDoc);
          encrypted = true;
          
          this.logAuditEntry({
            operation: 'encrypt',
            classification,
            userId,
            success: true,
            metadata: { fields: fieldsToEncrypt }
          });
        } catch (error) {
          Logger.error('Encryption failed during secure store', error instanceof Error ? error : new Error('Unknown error'));
          this.logAuditEntry({
            operation: 'encrypt',
            classification,
            userId,
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }

      // Apply masking
      if (fieldsToMask.length > 0) {
        try {
          const maskedDoc = dataMaskingService.maskDocument(securedDoc, fieldsToMask);
          Object.assign(securedDoc, maskedDoc);
          masked = true;
          
          this.logAuditEntry({
            operation: 'mask',
            classification,
            userId,
            success: true,
            metadata: { fields: fieldsToMask }
          });
        } catch (error) {
          Logger.error('Masking failed during secure store', error instanceof Error ? error : new Error('Unknown error'));
          this.logAuditEntry({
            operation: 'mask',
            classification,
            userId,
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }

      this.logAuditEntry({
        operation: 'store',
        classification,
        userId,
        success: true,
        metadata: { collection: collectionName, operationId }
      });

      return {
        success: true,
        operationId,
        timestamp: Date.now(),
        encrypted,
        masked,
        classification
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      Logger.error('Secure store failed', error instanceof Error ? error : new Error('Unknown error'));
      
      this.logAuditEntry({
        operation: 'store',
        classification,
        userId,
        success: false,
        error: errorMessage
      });

      return {
        success: false,
        operationId,
        timestamp: Date.now(),
        encrypted,
        masked,
        classification,
        error: errorMessage
      };
    }
  }

  async secureRetrieve(document: any, userId?: string): Promise<any> {
    if (!this.isInitialized) {
      throw new Error('Secure storage service not initialized');
    }

    const operationId = `retrieve_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    try {
      let decryptedDoc = { ...document };

      // Decrypt if needed
      if (document._encryptionMetadata && this.config.enableEncryption) {
        try {
          decryptedDoc = await dataEncryptionService.decryptDocument(document);
          
          this.logAuditEntry({
            operation: 'decrypt',
            classification: DataClassification.CONFIDENTIAL,
            userId,
            success: true
          });
        } catch (error) {
          Logger.error('Decryption failed during secure retrieve', error instanceof Error ? error : new Error('Unknown error'));
          this.logAuditEntry({
            operation: 'decrypt',
            classification: DataClassification.CONFIDENTIAL,
            userId,
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }

      this.logAuditEntry({
        operation: 'retrieve',
        classification: DataClassification.CONFIDENTIAL,
        userId,
        success: true,
        metadata: { operationId }
      });

      return decryptedDoc;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      Logger.error('Secure retrieve failed', error instanceof Error ? error : new Error('Unknown error'));
      
      this.logAuditEntry({
        operation: 'retrieve',
        classification: DataClassification.CONFIDENTIAL,
        userId,
        success: false,
        error: errorMessage
      });

      throw error;
    }
  }

  // Field configuration management
  configureField(fieldName: string, config: Partial<FieldSecurityConfig>): void {
    const existingConfig = this.fieldConfigs.get(fieldName) || {
      fieldName,
      classification: DataClassification.PUBLIC,
      encrypt: false,
      mask: false,
      required: false
    };

    const updatedConfig: FieldSecurityConfig = {
      ...existingConfig,
      ...config,
      fieldName
    };

    this.fieldConfigs.set(fieldName, updatedConfig);
    this.saveFieldConfigurations();
    
    Logger.info(`Field configuration updated for ${fieldName}`);
  }

  getFieldConfiguration(fieldName: string): FieldSecurityConfig | undefined {
    return this.fieldConfigs.get(fieldName);
  }

  getAllFieldConfigurations(): FieldSecurityConfig[] {
    return Array.from(this.fieldConfigs.values());
  }

  // Audit and monitoring
  getAuditLog(filters?: {
    operation?: string;
    classification?: DataClassification;
    userId?: string;
    startTime?: number;
    endTime?: number;
  }): AuditLogEntry[] {
    let filteredLog = [...this.auditLog];

    if (filters) {
      if (filters.operation) {
        filteredLog = filteredLog.filter(entry => entry.operation === filters.operation);
      }
      if (filters.classification) {
        filteredLog = filteredLog.filter(entry => entry.classification === filters.classification);
      }
      if (filters.userId) {
        filteredLog = filteredLog.filter(entry => entry.userId === filters.userId);
      }
      if (filters.startTime) {
        filteredLog = filteredLog.filter(entry => entry.timestamp >= filters.startTime!);
      }
      if (filters.endTime) {
        filteredLog = filteredLog.filter(entry => entry.timestamp <= filters.endTime!);
      }
    }

    return filteredLog.sort((a, b) => b.timestamp - a.timestamp);
  }

  getSecurityStats(): {
    totalOperations: number;
    successfulOperations: number;
    failedOperations: number;
    encryptionOperations: number;
    maskingOperations: number;
    classificationBreakdown: Record<string, number>;
  } {
    const totalOperations = this.auditLog.length;
    const successfulOperations = this.auditLog.filter(entry => entry.success).length;
    const failedOperations = totalOperations - successfulOperations;
    const encryptionOperations = this.auditLog.filter(entry => entry.operation === 'encrypt').length;
    const maskingOperations = this.auditLog.filter(entry => entry.operation === 'mask').length;
    
    const classificationBreakdown: Record<string, number> = {};
    this.auditLog.forEach(entry => {
      classificationBreakdown[entry.classification] = (classificationBreakdown[entry.classification] || 0) + 1;
    });

    return {
      totalOperations,
      successfulOperations,
      failedOperations,
      encryptionOperations,
      maskingOperations,
      classificationBreakdown
    };
  }

  // Configuration management
  updateConfig(newConfig: Partial<SecureStorageConfig>): void {
    this.config = { ...this.config, ...newConfig };
    Logger.info('Secure storage configuration updated');
  }

  getConfig(): SecureStorageConfig {
    return { ...this.config };
  }

  // Service status
  getStatus(): {
    isInitialized: boolean;
    encryptionEnabled: boolean;
    maskingEnabled: boolean;
    fieldConfigCount: number;
    auditLogSize: number;
  } {
    return {
      isInitialized: this.isInitialized,
      encryptionEnabled: this.config.enableEncryption,
      maskingEnabled: this.config.enableMasking,
      fieldConfigCount: this.fieldConfigs.size,
      auditLogSize: this.auditLog.length
    };
  }
}

// Export singleton instance
const secureDataStorageService = new SecureDataStorageService();
export default secureDataStorageService;
export { type StorageOperationResult, type AuditLogEntry, type FieldSecurityConfig };
